import { get, getCtx, set } from '@dimjs/utils';
import { utils } from '../../utils/index.js';
import { type WebviewJavascriptBridgeApi } from '../type.api.js';
import {
  type CallNtvData,
  type FabricCallbackData,
  type PlainObjectNoFunction,
} from '../type.builtin.js';
import { webCallbackToNtv } from './call.js';
import {
  type AndroidMessageFromNativeCall,
  type AndroidNativeCallbackWebData,
  type AndroidNativeCallWebData,
  type AndroidSendWebCallbackDataToNtv,
} from './types.android.js';

const { log, parse } = utils;

let receiveMessageQueue: null | Array<AndroidMessageFromNativeCall> = [];

/**
 * 保存Web端注册的方法结构, 方便native可以按需调用
 */
const registeredCallbacks = {
  /**
   * 保存同步注册的方法.
   */
  sync: {},
  /**
   * 保存异步注册的方法
   */
  async: {},
};

/**
 * 当前fabric是否已经ready初始化成功
 * @returns
 */
function isFullyInitialized() {
  return window['Fabric_Android_Init'] || false;
}

/**
 * Web端已经准备就绪, 如果Native有队列消息提前调用了客户端, 这个时候就可以处理已存储的队列消息.
 * Android/ios实现不同, iOS是将消息pending 到native直到web通知native准备就绪, 才执行.
 * Android 则是直接等bridge-js文件加载完毕直接发送消息到web, web端需要自己存储并合适时机去处理.
 * @param run
 */
function initOnce(run: Function): void {
  if (!isFullyInitialized()) {
    window['Fabric_Android_Init'] = true;
    // notify native that js apis register successfully on next event loop
    setTimeout(run);
  }
}
/**
 * Web端已经准备就绪, 如果Native有队列消息提前调用了客户端, 这个时候就可以处理已存储的队列消息.
 * Android/ios实现不同;
 * iOS是将消息pending 到native直到web通知native准备就绪, 才执行.
 * Android 则是直接等bridge-js文件加载完毕直接发送消息到web, web端需要自己存储并合适时机去处理.
 * Note: 如果webIsReady调用过晚, 可能会出现存储`receiveMessageQueue`队列的消息被消费2次. 在接收消息(handleMessageFromNative()), 第一次的消息
 * 会存起来, 同时也会 `dispatchMessageFromNative()` 如果消息同时被存起来, 同时也被直接消费成功那么当后续在调用webIsReady的时候就会存在同一个消息被消费2次的情况.
 * 不过WEB 注册的方式本质上是允许Native多次调用, 所以也不存在问题.
 */
export const webIsReady = () => {
  initOnce(() => {
    log.logData(
      '[android]当前bridge已经ready, 执行`panding`的`NtvCallWeb`的所有消息'
    );

    const receivedMessages = receiveMessageQueue;
    receiveMessageQueue = null;
    if (receivedMessages) {
      for (let i = 0; i < receivedMessages.length; i++) {
        dispatchMessageFromNative(receivedMessages[i]);
      }
    }
  });
};

/**
 * 注册Javasript API给Native端调用, 同名方法多次注册, 将会覆盖, 默认为异步注册.
 * 注意register的同一个`method`的`handler`永远是最后注册的一个, 如需注册多个handlers, 业务自行包装
 * @example
 * ```ts
 * register('a', function () {})
 * register('a.b', function () {})
 * register('a', { c: function () {} })
 * register('a.b', { c: function () {} })
 * ```
 * @param method 方法名, 字符串, 可以带命名空间e.g. `addValue`、`http.addValue`
 * @param fun 注册内容体
 * @param sync 是否为同步注册, 默认 `false`
 */
function register(
  method: string,
  fun: Function | PlainObjectNoFunction,
  type: 'sync' | 'async' = 'async'
) {
  const queue =
    type === 'sync' ? registeredCallbacks.sync : registeredCallbacks.async;
  const exist = queue[method] || get(queue, method);
  if (exist) {
    log.logWarn(`方法(${method})已经被注册, 使用新方法覆盖老方法!`);
  }
  set(queue, method, fun);
}
/**
 * 注册一个异步方法, 注意register的同一个`method`的`handler`永远是最后注册的一个, 如需注册多个handlers, 业务自行包装
 * @param method 方法名, 字符串, 可以带命名空间e.g. `addValue`、`http.addValue`
 * @param fun 注册内容体, 函数, 或者对象(里面包含多个函数)
 */
export const registerAsync: WebviewJavascriptBridgeApi['registerAsync'] = (
  name: string,
  fun
) => {
  register(name, fun, 'async');
};
/**
 * 注册一个同步方法, 注意register的同一个`method`的`handler`永远是最后注册的一个, 如需注册多个handlers, 业务自行包装
 * @param method 方法名, 字符串, 可以带命名空间e.g. `addValue`、`http.addValue`
 * @param fun 注册内容体, 函数, 或者对象(里面包含多个函数)
 */
export const registerSync: WebviewJavascriptBridgeApi['registerSync'] = (
  name,
  fun
) => {
  register(name, fun, 'sync');
};
/**
 * Native 检查javascript注册的回调里面是否存在此命名空间的方法
 * @param method 方法名, 命名空间名
 */
export const hasJavascriptMethod = (method: string): boolean => {
  const { async, sync } = registeredCallbacks;
  log.logData('hasJavascriptMethod', method, async, sync);
  return !!(
    sync[method] ||
    get(sync, method, false) ||
    async[method] ||
    get(async, method, false)
  );
};

/**
 * 判断当前Native通讯是Native回调WEB还是Native主动调用WEB接口.
 * @param message 通讯消息报文
 */
const isNtvCallbackToWeb = (
  message
): message is AndroidNativeCallbackWebData => {
  return message.responseId !== undefined;
};

/**
 * 处理WEB调用Native接口, Native处理完回调WEB的应用场景.
 * @param message Native处理完毕回调到Web的通讯数据报文
 */
function dispatchCallbackMessageFromNative(
  message: AndroidNativeCallbackWebData
) {
  setTimeout(() => {
    // 此处是Web端准备接收NTV端处理结果回调函数;
    const callbackFn = window[message.responseId];

    if (callbackFn) {
      log.logData(
        `[Android] WebCallNtv, 接收到Ntv异步返回数据内容:`,
        `isInitialized:`,
        isFullyInitialized(),
        `消息体:`,
        message,
        `Web端回调函数:`,
        callbackFn
      );

      // 此处为异步处理Native消息, 是否有必要增加标志位, 决定是否要删除callback, 比如文件下载进度, 这样的场景可能Native
      // 需要调用多次callback, 当标志为删除的时候在remove callback函数引用.
      if (message.complete !== false) {
        delete window[message.responseId];
      }
      callbackFn(parse.jsonParse<FabricCallbackData>(message.responseData));
    }
  });
}

/**
 * 处理Native主动调用Web注册的接口场景.
 * 当Native 主动调用H5序列化的时候如果是字符串是不会进行序列化处理的.
 * @param message Native主动调用Web的通讯数据报文
 */
function dispatchNtvCallMessageFromNative(message: AndroidNativeCallWebData) {
  setTimeout(() => {
    const { callbackId = '', data, handlerName = '' } = message;
    const ntvCallArgs: unknown = parse.jsonParse(data || '{}');
    const callbackToNtvData: AndroidSendWebCallbackDataToNtv = {
      callbackId,
      complete: true,
      data: {
        code: '0',
        data: null,
      },
    };
    const { async, sync } = registeredCallbacks;
    const syncFn = sync[handlerName] || get(sync, handlerName);
    const asyncFn = async[handlerName] || get(async, handlerName);

    if (syncFn) {
      log.logData(
        `[Android] 接收Ntv主动调用Web, 方法:(${handlerName})`,
        `isInitialized:`,
        isFullyInitialized(),
        '消息体:',
        message,
        'WEB同步方法:',
        syncFn
      );
      try {
        const syncCtx = sync[handlerName] ? sync : getCtx(sync, handlerName);
        callbackToNtvData.data = syncFn.apply(syncCtx, [ntvCallArgs]);
        webCallbackToNtv(callbackToNtvData);
      } catch (err) {
        log.logError(
          '[Android] dispatchNtvCallMessageFromNative() `syncFn.apply()` threw',
          err
        );
      }
    } else if (asyncFn) {
      log.logData(
        `[Android] 接收Ntv主动调用Web, 方法:(${handlerName})`,
        `isInitialized:`,
        isFullyInitialized(),
        '消息体:',
        message,
        'WEB异步方法:',
        asyncFn
      );
      const asyncCtx = async[handlerName] ? async : getCtx(async, handlerName);
      try {
        asyncFn.apply(asyncCtx, [
          ntvCallArgs,
          (data: FabricCallbackData<CallNtvData>, complete?: boolean) => {
            callbackToNtvData.data = data;
            callbackToNtvData.complete = complete !== false;
            webCallbackToNtv(callbackToNtvData);
          },
        ]);
      } catch (err) {
        log.logError(
          '[Android] dispatchNtvCallMessageFromNative() `asyncFn.apply()` threw',
          err
        );
      }
    } else {
      log.logError(
        '[Android] dispatchNtvCallMessageFromNative()',
        `No Registered Javascript Method (${handlerName}) Found In Web!`
      );
    }
  });
}

function dispatchMessageFromNative(message: AndroidMessageFromNativeCall) {
  // 此处是Web端准备接收NTV端处理结果回调函数;
  if (isNtvCallbackToWeb(message)) {
    dispatchCallbackMessageFromNative(message);
  } else {
    // 处理Native主动call
    dispatchNtvCallMessageFromNative(message);
  }
}
/**
 * 此处实现与ios有一定差异, 消息派发, 此处要处理Native 主动调用WEB的场景,
 * 同时也要兼容 WEB发起请求到Native, Native 处理完发送回调数据的场景.
 * @param message Native发送到Web的消息体
 */
export const handleMessageFromNative = (messageJSON: string) => {
  const message = parse.jsonParse<AndroidMessageFromNativeCall>(messageJSON);
  // 此处要注意, 收到的callback消息被缓存起来, 假设一个场景.
  // 1. navbar点击事件, 这个时候的NTV回掉的 message 包含了responseId, 并且会被存储到`messageQueue`里面去
  // 2. 页面有一个按钮点击事件里面执行了`ready(fn)`, 此时会调用`webIsReady()`方法, 因为这次ready执行的时机在navbar点击事件之后.
  // 3. 最终结果就是点击按钮的时候 重复处理了一次Native在`navbar`过程中callback得message体
  if (receiveMessageQueue && !isFullyInitialized()) {
    receiveMessageQueue.push(message);
  }
  // 处理消息派发.
  dispatchMessageFromNative(message);
};
